import { SandboxFunctionPersonalAuthCard } from "@app/components/actions/blocked/SandboxFunctionPersonalAuthCard";
import { SandboxFunctionToolApprovalCard } from "@app/components/actions/blocked/SandboxFunctionToolApprovalCard";
import { useVisualizationRetry } from "@app/hooks/conversations";
import { useEventSource } from "@app/hooks/useEventSource";
import { useSendNotification } from "@app/hooks/useNotification";
import type {
  SandboxFunctionMCPApproveExecutionEvent,
  SandboxFunctionToolPersonalAuthRequiredEvent,
} from "@app/lib/actions/mcp_internal_actions/events";
import { clientFetch } from "@app/lib/egress/client";
import { getErrorFromResponse } from "@app/lib/swr/swr";
import datadogLogger from "@app/logger/datadogLogger";
import type {
  PostSandboxFunctionInvocationRequestBody,
  PostSandboxFunctionInvocationResponseBody,
  SandboxFunctionCallError,
  SandboxFunctionInvocationEvent,
  SandboxFunctionInvocationType,
} from "@app/types/api/sandbox_functions";
import type {
  CallFunctionRequest,
  CommandResultMap,
  EditTextFn,
  VisualizationRPCCommand,
  VisualizationRPCRequest,
} from "@app/types/assistant/visualization";
import { isVisualizationRPCRequest } from "@app/types/assistant/visualization";
import { isAPIError } from "@app/types/error";
import { Err, Ok, type Result } from "@app/types/shared/result";
import {
  assertNever,
  assertNeverAndIgnore,
} from "@app/types/shared/utils/assert_never";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import {
  AlertCircle,
  Button,
  CodeBlock,
  ContentMessage,
  cn,
  Markdown,
  Sheet,
  SheetContainer,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Spinner,
} from "@dust-tt/sparkle";
import type React from "react";
import type { SetStateAction } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface BaseVisualization {
  complete: boolean;
  identifier: string;
}

type PublicVisualization = BaseVisualization & {
  accessToken: string | null;
  code?: undefined;
};

type ProtectedVisualization = BaseVisualization & {
  accessToken?: undefined;
  code: string;
};

export type Visualization = PublicVisualization | ProtectedVisualization;

export type FrameAccess = "conversation" | "public-anonymous" | "public-member";

const sendResponseToIframe = <T extends VisualizationRPCCommand>(
  request: { command: T } & VisualizationRPCRequest,
  response: CommandResultMap[T],
  target: MessageEventSource
) => {
  target.postMessage(
    {
      command: "answer",
      messageUniqueId: request.messageUniqueId,
      identifier: request.identifier,
      result: response,
    },
    { targetOrigin: "*" }
  );
};

const sendErrorToIframe = (
  request: CallFunctionRequest,
  error: SandboxFunctionCallError,
  target: MessageEventSource,
  {
    conversationId,
    workspaceId,
  }: { conversationId: string | null; workspaceId: string }
) => {
  // Once handed over, the error belongs to Frame code and only comes back if the Frame reports it
  // itself, so log here to keep a trace of every failed call.
  datadogLogger.info("Sandbox function call failed", {
    code: error.code,
    conversationId,
    errorMessage: error.message,
    fileId: request.identifier,
    functionIdOrSlug: request.params.functionIdOrSlug,
    status: error.status,
    workspaceId,
  });

  target.postMessage(
    {
      command: "answer",
      messageUniqueId: request.messageUniqueId,
      identifier: request.identifier,
      error,
    },
    { targetOrigin: "*" }
  );
};

const getExtensionFromBlob = (blob: Blob): string => {
  const mimeToExt: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "text/csv": "csv",
  };

  return mimeToExt[blob.type] || "txt"; // Default to 'txt' if mime type is unknown.
};

interface SandboxFunctionInvocationProps {
  workspaceId: string;
  functionId: string;
  invocationId: string;
  onSettle: (
    invocationId: string,
    result: Result<unknown, SandboxFunctionCallError>
  ) => void;
}

type SandboxFunctionBlockingEvent =
  | SandboxFunctionMCPApproveExecutionEvent
  | SandboxFunctionToolPersonalAuthRequiredEvent;

type QueuedBlockingEvent = {
  // SSE event id, unique per delivery. Removal targets this exact version: resolving an action can
  // produce a follow-up blocking event for the same actionId (e.g. approval then personal auth)
  // before the resolve response returns, and that newer entry must survive the removal.
  eventId: string;
  event: SandboxFunctionBlockingEvent;
};

// Consumes one invocation's event stream, settles the pending iframe call, and renders the
// approval or authentication card over the frame while a tool is blocked on user input.
function SandboxFunctionInvocation({
  workspaceId,
  functionId,
  invocationId,
  onSettle,
}: SandboxFunctionInvocationProps) {
  const [blockedActions, setBlockedActions] = useState<QueuedBlockingEvent[]>(
    []
  );

  const enqueueBlockedAction = useCallback(
    (eventId: string, event: SandboxFunctionBlockingEvent) => {
      setBlockedActions((prev) => {
        // The stream can re-deliver or supersede an event after a reconnect or a resolution:
        // replace by actionId, keeping the newest delivery.
        const existingIndex = prev.findIndex(
          (a) => a.event.actionId === event.actionId
        );
        const entry = { eventId, event };
        return existingIndex === -1
          ? [...prev, entry]
          : prev.map((a, index) => (index === existingIndex ? entry : a));
      });
    },
    []
  );

  const removeBlockedAction = useCallback((eventId: string) => {
    setBlockedActions((prev) => prev.filter((a) => a.eventId !== eventId));
  }, []);

  const buildEventSourceURL = useCallback(
    (lastEvent: string | null) => {
      const esURL = `/api/sse/w/${workspaceId}/sandbox-functions/${functionId}/invocations/${invocationId}/events`;
      let lastEventId = "";
      if (lastEvent) {
        const eventPayload: { eventId: string } = JSON.parse(lastEvent);
        lastEventId = eventPayload.eventId;
      }
      return esURL + "?lastEventId=" + lastEventId;
    },
    [workspaceId, functionId, invocationId]
  );

  const onEventCallback = useCallback(
    (eventStr: string) => {
      try {
        const eventPayload: {
          eventId: string;
          data: SandboxFunctionInvocationEvent;
        } = JSON.parse(eventStr);

        switch (eventPayload.data.type) {
          case "sandbox_function_invocation_created":
            // NO-OP
            break;
          case "sandbox_function_invocation_result":
            onSettle(invocationId, new Ok(eventPayload.data.result));
            break;
          case "sandbox_function_invocation_error":
            onSettle(invocationId, new Err(eventPayload.data.error));
            break;
          case "tool_approve_execution":
          case "tool_personal_auth_required":
            enqueueBlockedAction(eventPayload.eventId, eventPayload.data);
            break;
          default:
            assertNeverAndIgnore(eventPayload.data);
        }
      } catch (error) {
        onSettle(
          invocationId,
          new Err({
            code: "transport_error",
            message:
              "Failed to parse function invocation event: " +
              normalizeError(error).message,
          })
        );
      }
    },
    [invocationId, onSettle, enqueueBlockedAction]
  );

  const onTerminalError = useCallback(() => {
    onSettle(
      invocationId,
      new Err({
        code: "transport_error",
        message: "Failed to listen to function invocation events.",
      })
    );
  }, [invocationId, onSettle]);

  useEventSource(
    buildEventSourceURL,
    onEventCallback,
    `sandbox-function-invocation-${invocationId}`,
    { onTerminalError }
  );

  const blockedAction = blockedActions.at(0);
  if (!blockedAction) {
    return null;
  }

  let blockedActionCard: React.ReactNode;
  switch (blockedAction.event.type) {
    case "tool_approve_execution":
      blockedActionCard = (
        <SandboxFunctionToolApprovalCard
          key={blockedAction.eventId}
          event={blockedAction.event}
          onResolved={() => removeBlockedAction(blockedAction.eventId)}
        />
      );
      break;
    case "tool_personal_auth_required":
      blockedActionCard = (
        <SandboxFunctionPersonalAuthCard
          key={blockedAction.eventId}
          event={blockedAction.event}
          onResolved={() => removeBlockedAction(blockedAction.eventId)}
        />
      );
      break;
    default:
      assertNeverAndIgnore(blockedAction.event);
      blockedActionCard = null;
  }

  // Overlays the frame while the tool is blocked on user input. Cards are keyed by the delivery
  // eventId so consecutive events of the same type never reuse local card state.
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center overflow-auto bg-muted-foreground/75 p-4">
      <div className="flex w-full max-w-xl justify-center">
        {blockedActionCard}
      </div>
    </div>
  );
}

// Custom hook to encapsulate the logic for handling visualization messages.
function useVisualizationDataHandler({
  conversationId,
  createSandboxFunctionInvocation,
  getFileBlob,
  onEditText,
  setCodeDrawerOpened,
  setContentHeight,
  setErrorMessage,
  visualization,
  vizIframeRef,
  waitForSandboxFunctionInvocationResult,
  workspaceId,
}: {
  conversationId: string | null;
  createSandboxFunctionInvocation: (
    functionIdOrSlug: string,
    input?: unknown
  ) => Promise<Result<SandboxFunctionInvocationType, SandboxFunctionCallError>>;
  getFileBlob: (fileId: string) => Promise<Blob | null>;
  onEditText?: EditTextFn;
  setCodeDrawerOpened: (v: SetStateAction<boolean>) => void;
  setContentHeight: (v: SetStateAction<number>) => void;
  setErrorMessage: (v: SetStateAction<string | null>) => void;
  visualization: Visualization;
  vizIframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  waitForSandboxFunctionInvocationResult: (params: {
    functionId: string;
    invocationId: string;
  }) => Promise<Result<unknown, SandboxFunctionCallError>>;
  workspaceId: string;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { code } = visualization;

  const downloadFileFromBlob = useCallback(
    (blob: Blob, filename?: string) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      if (filename) {
        link.download = filename;
      } else {
        const ext = getExtensionFromBlob(blob);
        link.download = `visualization-${visualization.identifier}.${ext}`;
      }

      link.click();
      URL.revokeObjectURL(url);
    },
    [visualization.identifier]
  );

  useEffect(() => {
    const listener = async (event: MessageEvent) => {
      const { data } = event;

      const isOriginatingFromViz =
        event.source && event.source === vizIframeRef.current?.contentWindow;

      // Handle EXPORT_ERROR messages
      if (
        data.type === "EXPORT_ERROR" &&
        isOriginatingFromViz &&
        data.identifier === visualization.identifier
      ) {
        sendNotification({
          title: t("conv.vizExportFailedTitle", "Export Failed"),
          type: "error",
          description:
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            data.errorMessage ||
            t(
              "conv.vizExportError",
              "An error occurred while exporting the content."
            ),
        });
        return;
      }

      if (
        !isVisualizationRPCRequest(data) ||
        !isOriginatingFromViz ||
        data.identifier !== visualization.identifier
      ) {
        return;
      }

      switch (data.command) {
        case "callFunction": {
          const invocationRes = await createSandboxFunctionInvocation(
            data.params.functionIdOrSlug,
            data.params.input
          );

          if (invocationRes.isErr()) {
            sendErrorToIframe(data, invocationRes.error, event.source, {
              conversationId,
              workspaceId,
            });
            break;
          }

          const result = await waitForSandboxFunctionInvocationResult({
            functionId: invocationRes.value.functionId,
            invocationId: invocationRes.value.sId,
          });

          if (result.isErr()) {
            sendErrorToIframe(data, result.error, event.source, {
              conversationId,
              workspaceId,
            });
          } else {
            sendResponseToIframe(data, result.value, event.source);
          }
          break;
        }

        case "getFile":
          const fileBlob = await getFileBlob(data.params.fileId);

          sendResponseToIframe(data, { fileBlob }, event.source);
          break;

        case "getCodeToExecute":
          if (code) {
            sendResponseToIframe(data, { code }, event.source);
          }

          break;

        case "setContentHeight":
          setContentHeight(data.params.height);
          break;

        case "setErrorMessage":
          datadogLogger.info("Visualization error", {
            errorMessage: data.params.errorMessage,
            fileId: data.params.fileId,
            isInteractiveContent: data.params.isInteractiveContent,
          });
          setErrorMessage(data.params.errorMessage);
          break;

        case "downloadFileRequest":
          downloadFileFromBlob(data.params.blob, data.params.filename);
          break;

        case "displayCode":
          setCodeDrawerOpened(true);
          break;

        case "editText": {
          if (onEditText) {
            const editResult = await onEditText({
              newText: data.params.newText,
              oldText: data.params.oldText,
              targetFileId: data.params.targetFileId,
              source: data.params.source,
            });

            sendResponseToIframe(data, editResult, event.source);
          } else {
            sendResponseToIframe(
              data,
              { success: false, error: "Editing is not supported here" },
              event.source
            );
          }

          break;
        }

        default:
          assertNever(data);
      }
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [
    code,
    conversationId,
    createSandboxFunctionInvocation,
    downloadFileFromBlob,
    getFileBlob,
    onEditText,
    setContentHeight,
    setErrorMessage,
    setCodeDrawerOpened,
    visualization.identifier,
    vizIframeRef,
    sendNotification,
    waitForSandboxFunctionInvocationResult,
    workspaceId,
    t,
  ]);
}

export function CodeDrawer({
  isOpened,
  onClose,
  code,
}: {
  isOpened: boolean;
  onClose: () => void;
  code: string;
}) {
  const { t } = useTranslation();
  return (
    <Sheet
      open={isOpened}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent size="lg">
        <SheetHeader>
          <SheetTitle>
            {t("conv.vizCodeDrawerTitle", "Code for this visualization")}
          </SheetTitle>
        </SheetHeader>
        <SheetContainer>
          <CodeBlock className="language-jsx">{code}</CodeBlock>
        </SheetContainer>
      </SheetContent>
    </Sheet>
  );
}

interface VisualizationActionIframeProps {
  agentConfigurationId: string | null;
  conversationId: string | null;
  isEditable?: boolean;
  isInDrawer?: boolean;
  onEditText?: EditTextFn;
  frameAccess?: FrameAccess;
  spaceId?: string;
  visualization: Visualization;
  vizUrl: string;
  workspaceId: string;
}

export const VisualizationActionIframe = forwardRef<
  HTMLIFrameElement,
  VisualizationActionIframeProps
>(function VisualizationActionIframe(
  props: VisualizationActionIframeProps,
  ref
) {
  const { t } = useTranslation();
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryClicked, setRetryClicked] = useState(false);
  const [isCodeDrawerOpen, setCodeDrawerOpened] = useState(false);
  const vizIframeRef = useRef<HTMLIFrameElement | null>(null);

  // In-flight sandbox function invocations. Each entry mounts a
  // SandboxFunctionInvocation; the resolver of the pending `callFunction` promise is kept
  // in a ref so settling stays a pure state update.
  const [activeInvocations, setActiveInvocations] = useState<
    { functionId: string; invocationId: string }[]
  >([]);
  const invocationResolversRef = useRef<Map<
    string,
    (result: Result<unknown, SandboxFunctionCallError>) => void
  > | null>(null);
  if (invocationResolversRef.current === null) {
    invocationResolversRef.current = new Map();
  }
  const invocationResolvers = invocationResolversRef.current;

  const waitForSandboxFunctionInvocationResult = useCallback(
    ({
      functionId,
      invocationId,
    }: {
      functionId: string;
      invocationId: string;
    }) =>
      new Promise<Result<unknown, SandboxFunctionCallError>>((resolve) => {
        invocationResolvers.set(invocationId, resolve);
        setActiveInvocations((prev) => [...prev, { functionId, invocationId }]);
      }),
    [invocationResolvers]
  );

  const settleSandboxFunctionInvocation = useCallback(
    (
      invocationId: string,
      result: Result<unknown, SandboxFunctionCallError>
    ) => {
      const resolve = invocationResolvers.get(invocationId);
      invocationResolvers.delete(invocationId);
      setActiveInvocations((prev) =>
        prev.filter((invocation) => invocation.invocationId !== invocationId)
      );
      resolve?.(result);
    },
    [invocationResolvers]
  );

  // Combine internal ref with forwarded ref.
  const combinedRef = useCallback(
    (node: HTMLIFrameElement | null) => {
      vizIframeRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  const isErrored = !!errorMessage || retryClicked;

  const {
    agentConfigurationId,
    conversationId,
    isEditable = false,
    isInDrawer = false,
    onEditText,
    spaceId,
    visualization,
    workspaceId,
    frameAccess = "conversation",
  } = props;

  const isPublic = frameAccess !== "conversation";

  const getFileBlob = useCallback(
    async (fileId: string) => {
      let url: string;

      if (fileId.startsWith("conversation-") || fileId.startsWith("pod-")) {
        // Canonical scoped paths — the global endpoint resolves auth from the prefix.
        const encodedPath = fileId.split("/").map(encodeURIComponent).join("/");
        url = `/api/w/${workspaceId}/files/path/${encodedPath}`;
      } else if (fileId.startsWith("conversation/")) {
        // Legacy path: normalize to canonical using context conversationId.
        if (!conversationId) {
          return null;
        }
        const rel = fileId.slice("conversation/".length);
        url = `/api/w/${workspaceId}/files/path/conversation-${conversationId}/${rel}`;
      } else if (fileId.startsWith("pod/") || fileId.startsWith("project/")) {
        // Legacy paths: normalize to canonical using context spaceId.
        if (!spaceId) {
          return null;
        }
        const rel = fileId.startsWith("pod/")
          ? fileId.slice("pod/".length)
          : fileId.slice("project/".length);
        url = `/api/w/${workspaceId}/files/path/pod-${spaceId}/${rel}`;
      } else {
        url = `/api/w/${workspaceId}/files/${fileId}?action=view`;
      }

      const response = await clientFetch(url);
      if (!response.ok) {
        return null;
      }

      const resBuffer = await response.arrayBuffer();

      return new Blob([resBuffer], {
        type: response.headers.get("Content-Type") ?? undefined,
      });
    },
    [workspaceId, conversationId, spaceId]
  );

  const createSandboxFunctionInvocation = useCallback(
    async (
      functionIdOrSlug: string,
      input?: unknown
    ): Promise<
      Result<SandboxFunctionInvocationType, SandboxFunctionCallError>
    > => {
      try {
        if (frameAccess === "public-anonymous") {
          return new Err({
            code: "not_supported",
            message: "Pod functions are not supported in shared frames.",
          });
        }

        const body: PostSandboxFunctionInvocationRequestBody = {
          input,
          context: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        };

        const encodedFunctionIdOrSlug = encodeURIComponent(functionIdOrSlug);
        const response = await clientFetch(
          `/api/w/${workspaceId}/sandbox-functions/${encodedFunctionIdOrSlug}/invocations`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) {
          const error = await getErrorFromResponse(response);
          return new Err({
            // Forward the API error type verbatim. Re-deriving a code here would collapse every
            // failure but one into `invocation_failed` and drop the only classification the
            // endpoint produced.
            code: isAPIError(error) ? error.type : "invocation_failed",
            message: error.message,
            status: response.status,
          });
        }

        const result: PostSandboxFunctionInvocationResponseBody =
          await response.json();

        return new Ok(result.invocation);
      } catch (error) {
        return new Err({
          code: "transport_error",
          message: normalizeError(error).message,
        });
      }
    },
    [frameAccess, workspaceId]
  );

  useVisualizationDataHandler({
    conversationId,
    createSandboxFunctionInvocation,
    getFileBlob,
    onEditText,
    setCodeDrawerOpened,
    setContentHeight,
    setErrorMessage,
    visualization,
    vizIframeRef,
    waitForSandboxFunctionInvocationResult,
    workspaceId,
  });

  const { code, complete: codeFullyGenerated } = visualization;

  const iframeLoaded = contentHeight > 0;
  const showSpinner = useMemo(
    () => (codeFullyGenerated && !iframeLoaded && !isErrored) || retryClicked,
    [codeFullyGenerated, iframeLoaded, isErrored, retryClicked]
  );

  const { handleVisualizationRetry, canRetry } = useVisualizationRetry({
    workspaceId,
    conversationId,
    agentConfigurationId,
    isPublic,
  });

  const handleRetryClick = useCallback(async () => {
    if (retryClicked || !errorMessage) {
      return;
    }

    setRetryClicked(true);
    setErrorMessage(null);

    const success = await handleVisualizationRetry(errorMessage);
    if (!success) {
      setRetryClicked(false);
    }
  }, [errorMessage, handleVisualizationRetry, retryClicked]);

  const vizUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("identifier", visualization.identifier);

    if (visualization.accessToken) {
      params.set("accessToken", visualization.accessToken);
    }

    if (isInDrawer) {
      params.set("fullHeight", "true");
    }

    if (isEditable) {
      params.set("editable", "true");
    }

    return `${props.vizUrl.replace(/\/$/, "")}/content?${params.toString()}`;
  }, [visualization, isInDrawer, isEditable, props.vizUrl]);

  return (
    <div className={cn("relative flex flex-col", isInDrawer && "h-full")}>
      {code && (
        <CodeDrawer
          isOpened={isCodeDrawerOpen}
          onClose={() => setCodeDrawerOpened(false)}
          code={code}
        />
      )}
      {activeInvocations.map((invocation) => (
        <SandboxFunctionInvocation
          key={invocation.invocationId}
          workspaceId={workspaceId}
          functionId={invocation.functionId}
          invocationId={invocation.invocationId}
          onSettle={settleSandboxFunctionInvocation}
        />
      ))}
      <div
        className={cn(
          "relative w-full overflow-hidden",
          codeFullyGenerated && !isErrored && !isInDrawer && "min-h-96",
          errorMessage && "h-full",
          isInDrawer && "h-full"
        )}
      >
        <div className={cn("flex", isInDrawer && "h-full")}>
          {!codeFullyGenerated ? (
            <div className="flex h-full w-full shrink-0">
              <Markdown
                content={"```javascript\n" + (code ?? "") + "\n```"}
                isStreaming={!codeFullyGenerated}
                isLastMessage={true}
              />
            </div>
          ) : (
            <div
              className={cn(
                "relative flex w-full shrink-0 items-center justify-center",
                isInDrawer ? "h-full" : "h-panel"
              )}
            >
              {codeFullyGenerated && !isErrored && (
                <div
                  style={
                    isInDrawer
                      ? { minHeight: "200px" }
                      : {
                          height: `${contentHeight}px`,
                          minHeight: "96px",
                        }
                  }
                  className={cn(
                    "w-full",
                    isInDrawer ? "h-full" : "max-h-[600px]"
                  )}
                >
                  <iframe
                    ref={combinedRef}
                    className={cn(
                      "h-full w-full",
                      !errorMessage && !isInDrawer && "min-h-96"
                    )}
                    src={vizUrl}
                    sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                  />
                </div>
              )}

              {isErrored && !retryClicked && !isPublic && (
                <div
                  className={cn(
                    "flex w-full items-center justify-center p-6",
                    isInDrawer ? "h-full" : "h-panel"
                  )}
                >
                  <ContentMessage
                    title={t("conv.vizFailedTitle", "Visualization failed")}
                    variant="warning"
                    icon={AlertCircle}
                    className="max-w-md"
                  >
                    <div className="mb-4 text-sm">
                      {t(
                        "conv.vizFailedGeneratedCode",
                        "The visualization failed due to an error in the generated code."
                      )}
                    </div>

                    {errorMessage && (
                      <div className="mb-4 rounded-md bg-warning-50 p-3 text-xs text-warning-900">
                        {errorMessage}
                      </div>
                    )}

                    {canRetry && (
                      <Button
                        variant="outline"
                        label={t("conv.vizAskAgentToFix", "Ask agent to fix")}
                        onClick={handleRetryClick}
                        disabled={retryClicked}
                      />
                    )}
                  </ContentMessage>
                </div>
              )}

              {isErrored && isPublic && (
                <div className="flex h-full w-full items-center justify-center p-6">
                  <div className="flex flex-col gap-3 text-center">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle className="h-8 w-8" />
                        <p className="heading-xl leading-7 text-foreground">
                          {t("conv.vizErrorHeading", "Visualization Error")}
                        </p>
                      </div>
                      <p className="copy-sm leading-tight text-muted-foreground">
                        {t(
                          "conv.vizPublicError",
                          "This visualization encountered an error and cannot be displayed. Please contact the creator of this visualization for assistance."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showSpinner && (
        <div className="absolute inset-0 flex items-center justify-center bg-panel-background">
          <Spinner size="lg" />
        </div>
      )}
    </div>
  );
});
